import React, { useEffect, useState } from 'react';

import CardLayout from '@magoosh/layouts/card';
import { FontAwesomeIcon, SparkleIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { AnswerData } from 'types/answer';
import fetch from 'utilities/fetch';

import ContextualLink from '@test_prep/app/components/contextual_link';

interface Props {
  answer: AnswerData;
  inPracticeSession?: boolean;
  onFinished?: () => void;
  renderOnFinished?: boolean;
}

const ERROR = 'error';
const FINISHED = 'finished';
const PENDING_ANSWERS = 'pending_answers';
const PROCESSING = 'processing';

export default (props: Props) => {
  const [reportStatus, setReportStatus] = useState<string>(props.answer.scoreReport.status);

  useEffect(() => {
    if (props.inPracticeSession) return;
    if (reportStatus === FINISHED || reportStatus === ERROR) return;

    const poller = setInterval(() => {
      fetch(paths.api.gradingRequestStatus(props.answer.id)).then((result: any) => {
        if (result.gradingRequest.status !== reportStatus) {
          setReportStatus(result.gradingRequest.status);

          if (result.gradingRequest.status === FINISHED && props.onFinished) {
            props.onFinished();
          }

          if (result.gradingRequest.status === FINISHED || result.gradingRequest.status === ERROR) {
            clearInterval(poller);
          }
        }
      });
    }, 5000);

    return () => clearInterval(poller)
  }, []);

  const retryGrading = () => {
    fetch(paths.api.aiGradingRequestRegrade(props.answer.scoreReport.id), {
      method: 'POST',
    }).then((response) => {
      window.location.reload();
    });
  };

  if ((reportStatus === PROCESSING || reportStatus === PENDING_ANSWERS) && props.inPracticeSession) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <SparkleIcon color="#2A6EB6" size={20} />
          </div>

          <strong>
            We're grading your {props.answer.scoreReport.requestType} answer and your score report will be available at the end of the practice session.
          </strong>
        </div>
      </CardLayout.Card>
    );
  } else if (reportStatus === PROCESSING || reportStatus === PENDING_ANSWERS) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <FontAwesomeIcon className="fa-spin" icon="refresh" style={{ color: '#2A6EB6' }} />
          </div>

          <strong>
            We're grading your {props.answer.scoreReport.requestType} answer. This can take a minute or two.
          </strong>
        </div>
      </CardLayout.Card>
    );
  } else if (reportStatus === ERROR) {
    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <FontAwesomeIcon icon="exclamation-triangle" />
          </div>

          <div>
            <strong>
              We ran into an issue grading your answer.
              {' '}<a onClick={retryGrading} style={{ color: '#2A6EB6' }}>Retry grading</a>
              {' '}and if you are still having trouble, contact us at
              {' '}<a href="mailto:help@magoosh.com" style={{ color: '#2A6EB6' }}>help@magoosh.com</a>.
            </strong>
          </div>
        </div>
      </CardLayout.Card>
    );
  } else if (reportStatus === FINISHED && props.renderOnFinished) {
    const scoreReportPath = props.answer.scoreReport.requestType === 'essay' ? (
      paths.scoreReport(props.answer.scoreReport.id)
    ) : (
      paths.scoreReportAnswer(props.answer.scoreReport.id, props.answer.id)
    );

    return (
      <CardLayout.Card borderGradient style={{ padding: '15px 24px' }}>
        <div className="align-items-center d-flex gap-s" style={{ color: '#2A6EB6' }}>
          <div>
            <SparkleIcon color="#2A6EB6" size={20} />
          </div>

          <strong>
            Your {props.answer.scoreReport.requestType} answer was graded by Magoosh AI. View your
            {' '}<ContextualLink href={scoreReportPath} style={{ color: '#2A6EB6' }}>
              Score Report
            </ContextualLink>.
          </strong>
        </div>
      </CardLayout.Card>
    );
  } else {
    return null;
  }
};
